import { ApplicationLightResult, ApplicationResult, InternationalizationTitle, Node } from "@/types/generated/UsedGeneratedTypes";
import { Fetcher, LOCAL_STORAGE_LANG } from "./Fetcher";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import { LocalNode } from "@/types/front/LocalNode";
import { GenericTag } from "@/types/front/LocalTag";
import { LocalApplication } from "@/types/front/LocalApplication";
import { LocalApplicationLightResult } from "@/types/front/LocalApplicationLightResult";

type DataType = {
  id: string;
  name: string;
};

export class InternationalisationService extends Fetcher {
  static INSTANCE: InternationalisationService = new InternationalisationService();

  getDataTypeDisplay(reference: string, localThis?: unknown): string {
    const ctx = (localThis || this) as Record<string, unknown>;
    return `dataTypes.${ctx.dataTypeId}.internationalizationDisplay.${reference}.pattern`;
  }

  getSubmissionReferenceScopePath(authorizationScopes: string, localThis?: unknown): string {
    const ctx = (localThis || this) as Record<string, unknown>;
    return `data.${ctx.dataId}.submissions.referenceScopes.${authorizationScopes}`;
  }

  getLocaleforPath(application: ApplicationResult, path: string, defaultValue: string, forDescription?: boolean): string {
    if (!path?.length) {
      return defaultValue;
    }
    // On est obligé de typer any car navigateConfiguration va changer de type au fur et à mesure qu’on le parcourt
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let navigateConfiguration = application.internationalization as any;
    if (navigateConfiguration) {
      const pathArray = path.split(".");
      let pathItem = pathArray.shift();
      // on parcourt l’objet internationalization et on va récupérer à la fin :
      // soit un objet de traduction sur un tag dans la liste de tags de forme {"fr":"trad","en":"trad"})
      // soit un InternationalizationTitle
      while (pathItem) {
        navigateConfiguration = navigateConfiguration[pathItem];
        if (!navigateConfiguration) {
          return defaultValue;
        }
        pathItem = pathArray.shift();
      }
      return (
        ((forDescription ? navigateConfiguration.description : navigateConfiguration.title)
          || navigateConfiguration)[localStorage.getItem(LOCAL_STORAGE_LANG) || "french"]
          || (forDescription ? null : defaultValue)
      );
    }
    return defaultValue;
  }

  getLocale(): string | null {
    return localStorage.getItem(LOCAL_STORAGE_LANG);
  }

  mergeInternationalization(application: ApplicationResult): LocalApplicationResult {
    // TODO : vérifier si on a bien que ApplicationResult en entrée et si on peut se passer du code ci-dessous :
    // if (application?.configuration?.i18n) internationalization = application?.configuration?.i18n;
    // else if (application?.i18n) internationalization = application?.i18n;

    const internationalization = application.configuration?.i18n;
    const localApplicationResult = new LocalApplicationResult(application);

    if (!internationalization) {
      localApplicationResult.localName = application.name;
    }
    else {
      localApplicationResult.localName = this.localeApplicationNameTitle(
        internationalization.application,
        application.name,
      );
      localApplicationResult.localDescription = this.localeApplicationNameDescription(
        internationalization.application,
      );
    }
    return localApplicationResult;
  }

  mergeInternationalizationApplicationLightResult(applicationLightResult: ApplicationLightResult): LocalApplicationLightResult {
    const application = applicationLightResult.application as LocalApplication;
    const internationalization = application.configuration?.i18n;

    if (!internationalization) {
      application.localName = application.name;
      application.localDescription = "";
    }
    else {
      application.localName = this.localeApplicationNameTitle(
        internationalization.application,
        application.name,
      );
      application.localDescription = this.localeApplicationNameDescription(
        internationalization.application,
      );
    }
    const localApplicationLightResult = new LocalApplicationLightResult(applicationLightResult);
    localApplicationLightResult.application = application;
    return localApplicationLightResult;
  }

  localeApplicationNameTitle(applicationInternationalization: InternationalizationTitle, defautName: string): string {
    return (
      applicationInternationalization?.title?.[localStorage.getItem(LOCAL_STORAGE_LANG) ?? ""]
      ?? defautName
    );
  }

  localeApplicationNameDescription(applicationInternationalization: InternationalizationTitle): string {
    return applicationInternationalization?.description?.[localStorage.getItem(LOCAL_STORAGE_LANG) ?? ""];
  }

  localeDataTypeIdName(application: ApplicationResult, datatype: DataType, forDescription?: boolean): string {
    if (application?.configuration?.i18n?.data?.[datatype.id]?.i18n != null) {
      return application?.configuration?.i18n?.data?.[datatype.id].i18n?.[
        forDescription ? "description" : "title"
      ]?.[localStorage.getItem(LOCAL_STORAGE_LANG) ?? ""];
    }
    else {
      return datatype.name;
    }
  }

  localeReferenceNames(referenceName: string, application: ApplicationResult, forDescription?: boolean): string | undefined {
    if (application.internationalization) {
      const applicationReferences = application.internationalization.data;
      if (referenceName) {
        const defaultValue = forDescription ? null : referenceName;
        return (
          applicationReferences?.[referenceName]?.i18n?.[
            forDescription ? "description" : "title"
          ]?.[localStorage.getItem(LOCAL_STORAGE_LANG) ?? ""] ?? defaultValue
        );
      }
    }
  }

  localeReferenceColumnsNamesForReference(referenceColumnName: string, application: ApplicationResult, forDescription?: boolean): string {
    if (application.internationalization) {
      const applicationReferences = application.internationalization.data;
      if (referenceColumnName) {
        for (const applicationReference in applicationReferences) {
          if (applicationReference === referenceColumnName) {
            const defaultValue = forDescription ? null : referenceColumnName;
            return (
              applicationReferences[applicationReference].i18n?.[
                forDescription ? "description" : "title"
              ]?.[localStorage.getItem(LOCAL_STORAGE_LANG) ?? ""] ?? defaultValue
            );
          }
        }
        return referenceColumnName;
      }
    }
    return referenceColumnName;
  }

  localeReferenceColumnsNames(referenceName: string, columnName: string, application: ApplicationResult, forDescription?: boolean): string {
    let localColumnName;
    const exportHeader
      = application?.internationalization?.data?.[referenceName]?.components?.[columnName]
        ?.exportHeader;
    if (exportHeader) {
      const defaultValue = forDescription ? null : columnName;
      localColumnName
        = exportHeader?.[forDescription ? "description" : "title"]?.[
          localStorage.getItem(LOCAL_STORAGE_LANG) ?? ""
        ] ?? defaultValue;
    }
    else {
      localColumnName = columnName;
    }
    return localColumnName;
  }

  treeReferenceName(application: ApplicationResult): Record<string, Node> {
    if (application.references) {
      for (const referenceName in application.references) {
        application.references[referenceName] = new LocalNode(
          application.references[referenceName],
          (name: string) => this.localeDataNames(name, application),
          (name: string) => this.localeDataDescription(name, application),
          (name: string) => application?.configuration?.dataDescription?.[name]?.tags as GenericTag[] || [],
          (tagName: string) => this.getLocaleforPath(application, "tags." + tagName, tagName),
        );
      }
    }
    return application.references;
  }

  treeDataTypesName(application: ApplicationResult): Record<string, Node> {
    if (application.dataTypes) {
      for (const referenceName in application.dataTypes) {
        application.dataTypes[referenceName] = new LocalNode(
          application.dataTypes[referenceName],
          (name: string) => this.localeDataNames(name, application),
          (name: string) => this.localeDataDescription(name, application),
          (name: string) => application?.configuration?.dataDescription?.[name]?.tags as GenericTag[] || [],
          (tagName: string) => this.getLocaleforPath(application, "tags." + tagName, tagName),
        );
      }
    }
    return application.dataTypes;
  }

  localeDataNames(dataName: string, application: ApplicationResult): string {
    if (application.internationalization) {
      const applicationDatas = application.internationalization.data;
      if (dataName) {
        for (const applicationData in applicationDatas) {
          if (applicationData === dataName) {
            const defaultValue = dataName;
            return (
              applicationDatas[applicationData].i18n?.title?.[
                localStorage.getItem(LOCAL_STORAGE_LANG) ?? ""
              ] ?? defaultValue
            );
          }
        }
        return dataName;
      }
    }
    return dataName;
  }

  localeDataDescription(dataName: string, application: ApplicationResult): string {
    if (application.internationalization) {
      const applicationDatas = application.internationalization.data;
      if (dataName) {
        for (const applicationData in applicationDatas) {
          if (applicationData === dataName) {
            return (
              applicationDatas[applicationData].i18n?.description?.[
                localStorage.getItem(LOCAL_STORAGE_LANG) ?? ""
              ] ?? ""
            );
          }
        }
        return "";
      }
    }
    return "";
  }
}
